import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { usePostV2ProviderLabsBundlesMutation } from '../store/api';

interface SaveBundleDialogProps {
  open: boolean;
  onClose: () => void;
  testNames: string[];
  specialInstructions: string;
}

export const SaveBundleDialog: React.FC<SaveBundleDialogProps> = ({
  open,
  onClose,
  testNames,
  specialInstructions,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { labOrder } = useEventTracking();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [createBundle, { isLoading }] = usePostV2ProviderLabsBundlesMutation();

  const handleSave = async () => {
    if (!name.trim()) {
      enqueueSnackbar('Please enter a bundle name', { variant: 'error' });
      return;
    }

    if (testNames.length === 0) {
      enqueueSnackbar('Cannot save bundle with no tests', { variant: 'error' });
      return;
    }

    try {
      await createBundle({
        createCustomBundleRequest: {
          name: name.trim(),
          description: description.trim() || null,
          test_names: testNames,
          special_instructions: specialInstructions || null,
        },
      }).unwrap();

      // Track bundle saved
      labOrder.bundleSaved({
        bundle_name: name.trim(),
        test_count: testNames.length,
      });

      enqueueSnackbar('Custom bundle saved successfully!', { variant: 'success' });
      handleClose();
    } catch (error) {
      console.error('Failed to save bundle:', error);
      enqueueSnackbar('Failed to save custom bundle', { variant: 'error' });
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save as Custom Bundle</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Bundle Name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            required
            autoFocus
            disabled={isLoading}
            placeholder="e.g., Comprehensive Metabolic Panel"
            helperText="Give your bundle a descriptive name"
          />

          <TextField
            label="Description (Optional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            disabled={isLoading}
            placeholder="e.g., Standard panel for routine health checkup"
            helperText="Add details about when to use this bundle"
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Lab Tests ({testNames.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {testNames.map((test, index) => (
                <Chip key={index} label={test} size="small" variant="outlined" color="primary" />
              ))}
            </Box>
          </Box>

          {specialInstructions && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Special Instructions
              </Typography>
              <Alert severity="info" variant="outlined">
                <Typography variant="body2">{specialInstructions}</Typography>
              </Alert>
            </Box>
          )}

          {testNames.length === 0 && (
            <Alert severity="warning">No lab tests selected. Please select tests before saving.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || !name.trim() || testNames.length === 0}
          startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
        >
          {isLoading ? 'Saving...' : 'Save Bundle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};